'use client';
import React from 'react';

import Bookmaker from '@src/components/Icons/Bookmaker';

import CardMetric from '@src/components/Shared/CardMetric';
import FractionDisplay from '@src/components/Shared/FractionDisplay';
import NextLink from '@src/components/Shared/NextLink';

import useIsMobile from '@src/hooks/useIsMobile';
import useDebounce from '@src/hooks/useDebounce';

import ForceInput from '../ForceInput/ForceInput';
import UnitDropdown from '../UnitDropdown/UnitDropdown';
import { UnitTypes } from '../UnitDropdown/UnitDropdown.types';
import { ForceRateRatioCardPropTypes } from './ForceRateRatioCard.types';

const units: UnitTypes[] = [
  { id: 'newtons', name: 'Newtons', unit: 'N' },
  { id: 'pounds', name: 'Pounds', unit: 'lb' },
  { id: 'kilograms', name: 'Kilograms', unit: 'kg' },
];

const ForceRateRatioCard: React.FC<ForceRateRatioCardPropTypes> = ({}) => {
  const isMobile = useIsMobile();

  // Main values
  const [maxForceValue, setMaxForceValue] = React.useState('');
  const [rapidForceValue, setRapidForceValue] = React.useState('');

  // Mobile-only temp values
  const [tempMaxForceValue, setTempMaxForceValue] = React.useState('');
  const [tempRapidForceValue, setTempRapidForceValue] = React.useState('');

  const [unit, setUnit] = React.useState('newtons');

  const handleCalculate = () => {
    setMaxForceValue(tempMaxForceValue);
    setRapidForceValue(tempRapidForceValue);
  };

  const convertToNewtons = (value: string, fromUnit: string) => {
    const v = parseFloat(value);
    if (isNaN(v)) return 0;
    switch (fromUnit) {
      case 'pounds':
        return v / 0.224809;
      case 'kilograms':
        return v / 0.1019716;
      default:
        return v;
    }
  };

  const maxForceInNewtons = convertToNewtons(maxForceValue, unit);
  const rapidForceInNewtons = convertToNewtons(rapidForceValue, unit);

  // Now rapid / max * 100
  let ratio = 0;
  if (maxForceInNewtons !== 0 && !isNaN(rapidForceInNewtons)) {
    ratio = (rapidForceInNewtons / maxForceInNewtons) * 100;
  }

  const formattedRatio = ratio.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  // --- Debounce the formattedRatio by 3000 ms ---
  const debouncedRatio = useDebounce(formattedRatio, 3000);

  // Fire a tracking event whenever a valid debounced ratio result appears
  React.useEffect(() => {
    if (
      maxForceValue !== '' &&
      rapidForceValue !== '' &&
      !isNaN(Number(debouncedRatio)) &&
      isFinite(Number(debouncedRatio))
    ) {
      if (typeof window !== 'undefined' && Array.isArray(window.dataLayer)) {
        window.dataLayer.push({
          event: 'msk_calculator_calculation_performed',
          eventCategory: 'msk_calculator_usage',
          eventLabel: 'msk_calculator_force_rate_ratio',
          value: 1,
        });
      }
    }
    // Only re-run when debouncedRatio changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedRatio]);

  return (
    <CardMetric
      id='calculator-force-rate-ratio'
      title='Force-Rate Ratio'
      productIcons={[
        {
          alt: 'ForceFrame App Icon',
          url: 'https://images.ctfassets.net/98s79sqwuajy/7dxABhvlBmXLF0UuYpAdss/883fc648f9bc2f93d9d05d9095800e67/forceframe-app-icon.svg',
        },
        {
          alt: 'NordBord App Icon',
          url: 'https://images.ctfassets.net/98s79sqwuajy/6iS357n4siVSm6nFZeci6q/544cd567518357e813ac48f15dae44f4/nordbord-app-icon.svg',
        },
        {
          alt: 'Dynamo App Icon',
          url: 'https://images.ctfassets.net/98s79sqwuajy/5KQtYsCw5caSZvIuohrRmm/c174bb8572e3170009776e40c21f18e9/dynamo-app-icon.svg',
        },
        {
          alt: 'ForceDecks App Icon',
          url: 'https://images.ctfassets.net/98s79sqwuajy/1sZL8SrOEPwzDxDhnJCoy2/d3561b92560bdd3fe2ecd709b6f4604f/forcedecks-app-icon.svg',
        },
      ]}
    >
      <p className='mb-[2.2rem] text-[1.2rem]'>
        Compare peak force values from two conditions: one emphasizing maximal force and the other
        emphasizing rate of force development.
      </p>

      <FractionDisplay
        className='mb-[2rem]'
        label='Force-Rate Ratio'
        numerator='<strong className="font-medium block sm:inline">Peak Force</strong> (Rate of Force Development Cueing)'
        denominator='<strong className="font-medium block sm:inline">Peak Force</strong> (Maximal Force Cueing)'
        multiplier='× 100'
      />

      <UnitDropdown units={units} activeUnitId={unit} onChange={setUnit} />

      <div>
        {/* 1️⃣ Rapid force first */}
        <ForceInput
          className='mb-[1.4rem]'
          labelLines={[`Cue 'Rapid Force':`, 'Peak Force']}
          units={units}
          activeUnit={unit}
          value={isMobile ? tempRapidForceValue : rapidForceValue}
          onChange={(e) => {
            const v = e.target.value;
            isMobile ? setTempRapidForceValue(v) : setRapidForceValue(v);
          }}
        />

        {/* 2️⃣ Then maximal force */}
        <ForceInput
          className='mb-[1.4rem]'
          labelLines={[`Cue 'Maximal Force':`, 'Peak Force']}
          units={units}
          activeUnit={unit}
          value={isMobile ? tempMaxForceValue : maxForceValue}
          onChange={(e) => {
            const v = e.target.value;
            isMobile ? setTempMaxForceValue(v) : setMaxForceValue(v);
          }}
        />
      </div>

      <div className='mb-[3.1rem] flex items-center justify-between font-normal text-primary'>
        <span className='ml-auto text-[1.4rem] font-medium'>Force-Rate Ratio</span>
        <span className='ml-[2rem] border-l-[0.2rem] border-[#D9D9D9] pl-[2rem] text-[1.8rem] sm:text-[3rem]'>
          {isNaN(ratio) || !isFinite(ratio) ? '0.0' : formattedRatio}
        </span>
      </div>

      <div className='relative flex items-center gap-[1rem]'>
        <div className='absolute inset-x-[-9999px] top-[-2rem] h-[20rem] bg-[#F8F8F8]' />

        <div className='flex w-full items-center gap-[1.6rem] text-[1.2rem]'>
          <Bookmaker className='relative z-10 hidden h-[2rem] w-[1.6rem] max-w-[1.6rem] shrink-0 text-primary md:block' />

          <p className='tet-pretty'>
            Learn from sport science leaders about peak force and{' '}
            <NextLink
              className='font-light underline'
              href='https://www.globalperformanceinsights.com/post/understanding-rate-of-force-development-rfd'
            >
              rate of force development
            </NextLink>{' '}
            as integral parts of assessment.
          </p>
        </div>

        {isMobile && (
          <button
            onClick={handleCalculate}
            className='common-btn-transition right-0 bottom-0 relative ml-auto inline-flex h-[3.7rem] max-h-[3.7rem] items-center justify-center rounded-[0.6rem] border-primary bg-primary px-[2rem] py-[0.8rem] text-[1.6rem] font-normal leading-[1.6rem] tracking-[-0.0304rem] text-[#fff]! hover:border-primary--v2 hover:bg-primary--v2 hover:text-primary--v2'
            disabled={
              tempMaxForceValue === maxForceValue && tempRapidForceValue === rapidForceValue
            }
          >
            Calculate
          </button>
        )}
      </div>
    </CardMetric>
  );
};

export default ForceRateRatioCard;
